import { ApolloError, useQuery } from '@apollo/client';
import { useAuth } from '@guildeducationinc/guild-auth';
import { datadogRum } from '@datadog/browser-rum';
import { Server } from '@src/components/GqlProvider/GqlProvider';
import {
  GET_PROFILE_COMPLETION_USER_DATA,
  ProfileCompletion_UserData,
  ProfileCompletion_UserDataRequest,
} from '@src/graphql/home/queries';

const ADVANCED_DEGREES = ['bachelors_degree', 'advanced_degree'];
const WALMART_EMPLOYER_ID = '6637b2af-eceb-4de9-8789-82e5b8b36200';

/**
 * Fetch profile completion data and compute transition status and funding level
 *
 * @packageDocumentation
 */

const useGetProfileCompletionUserData = () => {
  const { user, hasIncompleteProfile } = useAuth();

  const onError = (error: ApolloError): void => {
    datadogRum.addError(error, {
      message: `There was an error getting profile completion data: ${error.message}`,
    });
  };
  const { error, data, loading } = useQuery<
    ProfileCompletion_UserData,
    ProfileCompletion_UserDataRequest
  >(GET_PROFILE_COMPLETION_USER_DATA, {
    context: {
      server: Server.GLOBAL_APP_SYNC_ID_TOKEN,
    },
    variables: {
      uuid: user?.id || '',
    },
    skip: !user?.id,
    onError,
  });

  const computeBachelorsWarning = (degree: string) => {
    if (degree && user?.employerId === WALMART_EMPLOYER_ID && ADVANCED_DEGREES.includes(degree)) {
      return true;
    }
    return false;
  };

  // Only applies to Walmart
  const showBachelorsWarning =
    data && data.profileCompletion_getProfile
      ? computeBachelorsWarning(data.profileCompletion_getProfile.prior_education_experience!)
      : false;

  const isDPPEnrolled =
    data?.profileCompletion_getProfile?.post_profile_completion_route === 'sponsorship';
  const isTREnrolled =
    data?.profileCompletion_getProfile?.post_profile_completion_route === 'tuition_reimbursement';

  return {
    isProfileComplete: !hasIncompleteProfile,
    isDPPEnrolled,
    isTREnrolled,
    showBachelorsWarning,
    loading,
    error,
  };
};

export default useGetProfileCompletionUserData;
